import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, FaPlus, FaTrash, FaSave, FaSync, FaSearch, FaUserTag,
  FaCheckCircle, FaTimesCircle, FaChevronRight, FaChevronDown
} from "react-icons/fa";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";

const DEFAULT_ROLE_NAME = "Club Member";

const API = {
  rolesFromMemberBasic: "https://dev.sliitleo.org/Backend/roles_from_memberbasic.php",
  paths:                "https://dev.sliitleo.org/Backend/paths.php",
  rolePathsByName:      "https://dev.sliitleo.org/Backend/role_paths_by_name.php",
  roleAssign:           "https://dev.sliitleo.org/Backend/role_assign.php",
};

const FALLBACK_PATHS = [
  { key: "home",            label: "Home",              route: "/home" },
  { key: "profile",         label: "My Profile",        route: "/profile" },
  { key: "education",       label: "Leo Education",     route: "/education" },
  { key: "tickets",         label: "My Tickets",        route: "/tickets" },
  { key: "calendar",        label: "Club Calendar",     route: "/club-calander" },
  { key: "admin",           label: "Admin Console",     route: "/admin" },
  { key: "projects",        label: "Club Projects",     route: "/club-projects" },
  { key: "dashboard",       label: "Club Dashboard",    route: "/club-dashboard" },
  { key: "reporting",       label: "Reporting Hub",     route: "/reporting-hub" },
  { key: "installation",    label: "Installation",      route: "/installation" },
  { key: "education_admin", label: "Education Admin",   route: "/education-statistics" },
  { key: "attendance",      label: "Attendance",        route: "/attendance" },
];

/* Tiny toast without extra libs */
const Toast = ({ type = "ok", msg }) => (
  <div className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2
      ${type === "ok" ? "bg-emerald-600/20 text-emerald-200 ring-1 ring-emerald-400/30"
                      : "bg-rose-600/20 text-rose-200 ring-1 ring-rose-400/30"}`}>
    {type === "ok" ? <FaCheckCircle/> : <FaTimesCircle/>}{msg}
  </div>
);

export default function RolePathManagement() {
  const [roles, setRoles] = useState([]);                 // array of strings (role names)
  const [paths, setPaths] = useState([]);
  const [activeRoleName, setActiveRoleName] = useState(""); // string
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [newRoleName, setNewRoleName] = useState(DEFAULT_ROLE_NAME);
  const [renameMap, setRenameMap] = useState({});         // not used to edit MemberBasic roles (read-only list), but kept if you later add CRUD
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState({ emailOrId: "", roleName: "" });
  const [toast, setToast] = useState(null);
  const [rolePanelOpen, setRolePanelOpen] = useState(true);

  const showToast = useCallback((msg, type="ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2000);
  }, []);

  // bootstrap
  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const [rRes, pRes] = await Promise.allSettled([
          fetch(API.rolesFromMemberBasic),
          fetch(API.paths),
        ]);

        let roleNames = [];
        if (rRes.status === "fulfilled" && rRes.value.ok) {
          roleNames = await rRes.value.json(); // ["Club Member", "Admin", ...]
        }

        let pData = [];
        if (pRes.status === "fulfilled" && pRes.value.ok) {
          pData = await pRes.value.json();
        } else {
          pData = FALLBACK_PATHS;
        }

        setRoles(roleNames);
        setPaths(pData);

        // choose default active role: Club Member if present, else first, else empty
        const preferred = roleNames.find(r => r.toLowerCase() === DEFAULT_ROLE_NAME.toLowerCase());
        setActiveRoleName(preferred || roleNames[0] || "");
      } catch {
        setPaths(FALLBACK_PATHS);
        setRoles([]);
        setActiveRoleName("");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  // when active role changes, load its path keys
  useEffect(() => {
    if (!activeRoleName) {
      setSelectedKeys(new Set());
      return;
    }
    (async () => {
      try {
        const u = new URL(API.rolePathsByName);
        u.searchParams.set("role", activeRoleName);
        const res = await fetch(u.toString());
        if (!res.ok) throw new Error();
        const data = await res.json(); // [{ key }]
        setSelectedKeys(new Set((data || []).map(x => x.key)));
      } catch {
        setSelectedKeys(new Set());
      }
    })();
  }, [activeRoleName]);

  const filteredPaths = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return paths;
    return paths.filter(p =>
      p.label.toLowerCase().includes(q) ||
      p.route.toLowerCase().includes(q) ||
      p.key.toLowerCase().includes(q)
    );
  }, [paths, search]);

  const toggleKey = (key) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const selectAll = () => setSelectedKeys(new Set(paths.map(p => p.key)));
  const clearAll  = () => setSelectedKeys(new Set());

  const saveRolePaths = async () => {
    if (!activeRoleName) return;
    setSaving(true);
    try {
      const res = await fetch(API.rolePathsByName, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: activeRoleName, keys: [...selectedKeys] })
      });
      if (!res.ok) throw new Error();
      showToast("Access map saved");
    } catch {
      showToast("Failed to save", "err");
    } finally {
      setSaving(false);
    }
  };

  // Assign role to user (uses existing endpoint that updates MemberBasic.Role)
  const assignRole = async () => {
    const { emailOrId, roleName } = assignTarget;
    if (!emailOrId || !roleName) return;
    try {
      const res = await fetch(API.roleAssign, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailOrId, roleId: roleName }) // if your role_assign expects roleId, switch it to accept roleName or map it server-side
      });
      if (!res.ok) throw new Error();
      setAssignOpen(false);
      setAssignTarget({ emailOrId: "", roleName: "" });
      showToast("Role assigned to user");
    } catch {
      showToast("Assign failed", "err");
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1224] text-white">
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-0 lg:ml-64">
          <Topbar user={JSON.parse(localStorage.getItem("user")||"{}")} />

          {/* Header */}
          <div className="px-5 sm:px-7 md:px-10 pt-6 pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-2xl bg-indigo-500/10 ring-1 ring-indigo-400/20">
                  <FaShieldAlt className="text-indigo-300" size={20}/>
                </div>
                <div>
                  <h1 className="text-xl font-bold">Role & Path Management</h1>
                  <p className="text-xs text-white/60">
                    Roles are read from <b>MemberBasic.Role</b>. Default focus: <b>{DEFAULT_ROLE_NAME}</b>.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setAssignOpen(true)}
                  className="px-3 py-2 rounded-xl bg-teal-500/20 hover:bg-teal-500/30 ring-1 ring-teal-400/30 text-sm flex items-center gap-2"
                >
                  <FaUserTag/> Assign role to user
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-white/10 text-sm flex items-center gap-2"
                >
                  <FaSync/> Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-5 sm:px-7 md:px-10 pb-10 grid grid-cols-1 lg:grid-cols-12 gap-5">

            {/* Roles panel (list from MemberBasic) */}
            <div className="lg:col-span-4">
              <div className="rounded-2xl bg-[#0f172a] ring-1 ring-white/10 shadow-[0_20px_60px_-30px_rgba(0,0,0,.6)]">
                <button
                  onClick={() => setRolePanelOpen(v => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 border-b border-white/10"
                >
                  <span className="text-sm font-semibold">Roles (from MemberBasic)</span>
                  {rolePanelOpen ? <FaChevronDown/> : <FaChevronRight/>}
                </button>

                {rolePanelOpen && (
                  <div className="p-4 space-y-3">
                    <div className="max-h-[56vh] overflow-auto pr-1 space-y-2">
                      {roles.length === 0 && (
                        <div className="text-xs text-white/50">
                          No roles found in MemberBasic.Role. Add some users with a role value (e.g., “{DEFAULT_ROLE_NAME}”, “Admin”) and refresh.
                        </div>
                      )}
                      {roles.map((name) => (
                        <button
                          key={name}
                          onClick={() => setActiveRoleName(name)}
                          className={`w-full text-left p-2 rounded-xl border ${
                            activeRoleName === name
                              ? "border-indigo-400/40 bg-indigo-500/10"
                              : "border-white/10 bg-black/10 hover:bg-white/10"
                          }`}
                        >
                          <div className="text-sm font-medium">{name}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Path toggles */}
            <div className="lg:col-span-8">
              <div className="rounded-2xl bg-[#0f172a] ring-1 ring-white/10 shadow-[0_20px_60px_-30px_rgba(0,0,0,.6)]">
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                  <div className="px-3 py-1.5 rounded-xl bg-white/5 ring-1 ring-white/10 text-xs">
                    Active role: <b>{activeRoleName || "—"}</b>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <FaSearch className="absolute left-2 top-2.5 text-white/40" size={12}/>
                      <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search path"
                        className="pl-7 pr-3 py-2 rounded-xl bg-[#101a33] border border-white/10 text-sm outline-none"
                      />
                    </div>
                    <button onClick={selectAll} className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-xs">Select all</button>
                    <button onClick={clearAll}  className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-xs">Clear</button>
                    <button
                      onClick={saveRolePaths}
                      disabled={saving || !activeRoleName}
                      className={`px-3 py-2 rounded-xl text-sm flex items-center gap-2
                        ${saving ? "bg-indigo-900/60" : "bg-indigo-600 hover:bg-indigo-500"} `}
                    >
                      <FaSave/>{saving ? "Saving..." : "Save access"}
                    </button>
                  </div>
                </div>

                <div className="p-4 grid sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                  {loading ? (
                    [...Array(6)].map((_,i)=>(
                      <div key={i} className="h-16 rounded-xl bg-white/5 animate-pulse"/>
                    ))
                  ) : (
                    filteredPaths.map(p => {
                      const enabled = selectedKeys.has(p.key);
                      return (
                        <motion.label
                          key={p.key}
                          whileTap={{ scale: 0.98 }}
                          className={`cursor-pointer rounded-xl border px-3 py-3 flex items-start gap-3
                           ${enabled ? "border-emerald-400/40 bg-emerald-500/10" : "border-white/10 bg-white/5"}`}
                        >
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={() => toggleKey(p.key)}
                            className="mt-1 accent-emerald-500"
                          />
                          <div className="text-sm">
                            <div className="font-semibold">{p.label}</div>
                            <div className="text-white/50 text-[11px]">{p.route}</div>
                            <div className="text-white/40 text-[10px]">key: {p.key}</div>
                          </div>
                        </motion.label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

          </div>

          {/* Toast */}
          {toast && (
            <div className="fixed bottom-4 right-4 z-50">
              <Toast type={toast.type} msg={toast.msg}/>
            </div>
          )}

          {/* Assign modal */}
          {assignOpen && (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="w-full max-w-md rounded-2xl bg-[#0f172a] ring-1 ring-white/10 p-4">
                <div className="text-lg font-semibold mb-1">Assign Role to User</div>
                <p className="text-xs text-white/60 mb-3">Enter Email or SLIITLeoID and choose role.</p>
                <div className="space-y-3">
                  <input
                    value={assignTarget.emailOrId}
                    onChange={e => setAssignTarget(t => ({ ...t, emailOrId: e.target.value }))}
                    placeholder="Email or SLIITLeoID"
                    className="w-full px-3 py-2 rounded-xl bg-[#101a33] border border-white/10 text-sm outline-none"
                  />
                  <select
                    value={assignTarget.roleName || ""}
                    onChange={e => setAssignTarget(t => ({ ...t, roleName: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl bg-[#101a33] border border-white/10 text-sm outline-none"
                  >
                    <option value="">Select role…</option>
                    {roles.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <div className="flex justify-end gap-2 pt-1">
                    <button onClick={()=>setAssignOpen(false)} className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-sm">Cancel</button>
                    <button onClick={assignRole} className="px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-sm">Assign</button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}
